/**
 * @module  support/shared/widgets
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

import { Component, Input, OnDestroy } from '@angular/core';
import { GlobalErrorService } from '@support/shared/widgets/services/global-error.service';
import { AppConfig } from '@root/app.config';
import { delay, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

/**
 * A T-mobile custom design spinner component.
 */
@Component({
  selector: 'tmo-spinner',
  templateUrl: './spinner.component.html',
  styleUrls: ['../../../../../../style-sheets/components/html-spinner.component.scss'],
})
export class SpinnerComponent implements OnDestroy {
  /** This flag will be passed from the parent component to hide/display the spinner */
  public showSpinner: boolean;

  /** To set the value of showSpinner  */
  private toggleValue: boolean;

  /**
   * Numeric input property to set the delay time
   */
  @Input() public delay: number;
  /**
   * id is used to set to dom
   */
  @Input() public id: string;

  /**
   * spinner element to be used to remove after settime out
   */
  public spinner: HTMLElement;

  /**
   * Unsubscriber for all subscribtions
   */
  public ngUnsubscribe: Subject<void>;

  @Input() public spinnerTextMessage: string;

  constructor(
    private globalErrorService: GlobalErrorService,
    private appConfig: AppConfig
  ) {
    this.ngUnsubscribe = new Subject<void>();
    this.showSpinner = true;
    const spinnerHandlerConfig = this.appConfig.getConfig('spinnerHandlerConfig');
    const spinnerRemovalDelay = spinnerHandlerConfig ? spinnerHandlerConfig.waitBeforeRemoval : 0;
    this.globalErrorService.clearSpinnerObservable
      .pipe(delay(spinnerRemovalDelay), takeUntil(this.ngUnsubscribe))
      .subscribe((route: string) => {
        if (route === window.location.pathname) {
          this.showSpinner = true;
        }
      });
  }

  /**
   * Destroys all the subscriptions.
   * @returns void
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Used to set the delay to spinner component
   *
   * @memberof SpinnerComponent
   */
  @Input()
  public set setDelay(value: boolean) {
    this.toggleValue = value;
    if (this.toggleValue) {
      this.showSpinner = this.toggleValue;
    } else {
      setTimeout(() => {
        this.showSpinner = this.toggleValue;
      }, this.delay);
    }
  }

  /**
   * Used to set the delay to spinner component
   * @memberof SpinnerComponent
   */
  @Input()
  public set appendSpinnerToBody(value: boolean) {
    // @TODO: Need to cover test cases by creating dummy HTML by creating new pattern
    if (this.id) {
      const spinner = document.getElementById(this.id);
      if (spinner) {
        if (value) {
          document.body.appendChild(spinner);
        } else {
          setTimeout(() => {
            document.body.removeChild(spinner);
          }, 0);
        }
      }
    }
  }
}
